import { Outlet, useLocation, useNavigate, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import NavigateNext from "@mui/icons-material/NavigateNext";
import {
  Box,
  Breadcrumbs,
  Card,
  CircularProgress,
  Link as MuiLink,
  Stack,
  Tab,
  Tabs,
  TabsProps,
  Typography,
} from "@mui/material";
import { useQuery } from "react-query";
import { useEffect } from "react";

import { CustomerRemark } from "@/features/member/pages/MemberProfilePage/CustomerRemark";
import { MemberProduct } from "@/features/member/pages/MemberProfilePage/MemberProduct";
import { MemberProfile } from "@/features/member/pages/MemberProfilePage/MemberProfile";
import { getDocumentTitle, useRequireParams } from "@/utils";
import { getMember } from "@/services/member";

export function useMember(id: string) {
  return useQuery(`member_${id}`, () => getMember(+id));
}

export function TrainerMemberProfilePage() {
  const { id: trainerId, memberId } = useRequireParams(["id", "memberId"]);
  const location = useLocation();
  const navigate = useNavigate();
  const path = location?.pathname ?? "";

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const onChangeTab: TabsProps["onChange"] = (_, value: string) => {
    navigate(value, { replace: true });
  };

  const { data: profileData, isFetching } = useMember(memberId);

  const isLoaded = !isFetching && typeof profileData !== "undefined";
  const title = profileData?.code ?? "";
  const trainerDetailPath = `/trainers/${trainerId}`;

  return (
    <Box>
      <Helmet>
        <title>{getDocumentTitle(title)}</title>
      </Helmet>
      <Breadcrumbs
        separator={<NavigateNext fontSize="small" />}
        aria-label="breadcrumb"
        sx={{ mb: 2 }}
      >
        <MuiLink component={Link} color="text.secondary" to="..">
          เทรนเนอร์
        </MuiLink>
        <MuiLink component={Link} color="text.secondary" to="..">
          รายชื่อเทรนเนอร์
        </MuiLink>
        <MuiLink component={Link} color="text.secondary" to={trainerDetailPath}>
          ข้อมูลเทรนเนอร์
        </MuiLink>
        <Typography color="text.primary">ข้อมูลสมาชิก</Typography>
      </Breadcrumbs>
      <Stack spacing={3}>
        {isLoaded ? (
          <>
            <MemberProfile data={profileData} />
            {profileData.customerId && (
              <CustomerRemark
                data={profileData.latestRemark}
                customerId={profileData.customerId}
                queryKey={`member_${memberId}`}
              />
            )}
          </>
        ) : (
          <Card>
            <Box display="grid" sx={{ placeItems: "center" }} height={1192}>
              <CircularProgress disableShrink />
            </Box>
          </Card>
        )}
        <MemberProduct data={profileData} isLoading={isFetching} />
        <Box>
          <Tabs value={path} sx={{ mb: 2 }} onChange={onChangeTab}>
            <Tab
              label="ประวัติการจองคลาส"
              value={`${trainerDetailPath}/members/${memberId}`}
              id="booking"
            />
            <Tab
              label="ประวัติการจองเทรนเนอร์"
              value={`${trainerDetailPath}/members/${memberId}/pt`}
            />
            <Tab
              label="ประวัติการชำระเงิน"
              value={`${trainerDetailPath}/members/${memberId}/transactions`}
            />
          </Tabs>
          <Outlet />
        </Box>
      </Stack>
    </Box>
  );
}
